import { Fragment } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * NotesList Component
 * 
 * Displays a list of recent notes with titles and dates
 * 
 * @param {Array} notes - Array of note objects
 * @param {Function} onSelectNote - Function to call when a note is selected
 * @param {String} selectedNoteId - ID of the currently selected note
 * @returns {JSX.Element}
 */
const NotesList = ({ notes, onSelectNote, selectedNoteId, loading = false }) => {
  const theme = useTheme();

  // Format date to a readable string (e.g. "June 9, 2025")
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4} py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      {notes.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
          <Typography color="textSecondary">No saved notes yet</Typography>
        </Box>
      ) : (
        <List sx={{ width: '100%', padding: 0 }}>
          {notes.map((note, index) => (
            <Fragment key={note.id}>
              <ListItemButton
                selected={selectedNoteId === note.id}
                onClick={() => onSelectNote(note)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 'bold', mb: 0 }}
                    >
                      {note.title || 'Untitled Note'}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0 }}>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(note.created_at)}
                      </Typography>
                      {note.note_references && note.note_references.length > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          {note.note_references.length} reference{note.note_references.length !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
              {index < notes.length && <Divider />}
            </Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default NotesList;
